/**
 * TaskFormToolbar — Clone / Save Template / Load Template / LLM Prefill / Preview
 *
 * Renders a compact action bar between the form header and the tab strip.
 * The Preview button opens a right-side drawer showing the complete task
 * template as JSON or structured text — all fields on one screen.
 */

import { useState, useCallback } from 'react';
import {
  ActionIcon,
  CopyButton,
  Drawer,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBraces,
  IconCheck,
  IconCopy,
  IconDeviceFloppy,
  IconFileImport,
  IconSparkles,
} from '@tabler/icons-react';
import React from 'react';
import type { TaskFormState } from './taskFormState';

export interface TaskFormToolbarProps {
  /** Collect the current form values into a serializable state */
  collectCurrentState: () => TaskFormState;
  /** Called when user clicks Clone — receives state with id stripped */
  onClone?: (state: TaskFormState) => void;
  /** Open the Save Template modal */
  onSaveTemplate?: () => void;
  /** Open the Load Template / Built-in picker */
  onLoadTemplate?: () => void;
  /** Open the LLM Prefill panel */
  onLlmPrefill?: () => void;
}

// ============================================================================
// Simple JSON -> structured text converter (no dependencies)
// ============================================================================

function toStructuredText(value: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (value === null || value === undefined) return `${pad}null`;
  if (typeof value === 'boolean') return `${pad}${value}`;
  if (typeof value === 'number') return `${pad}${value}`;
  if (typeof value === 'string') {
    if (value.includes('\n')) {
      const lines = value.split('\n').map(l => `${pad}  ${l}`).join('\n');
      return `${pad}|\n${lines}`;
    }
    if (/^(true|false|null|[-\d])/.test(value) || value.includes(':') || value.includes('#')) {
      return `${pad}"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
    }
    return `${pad}${value}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return `${pad}[]`;
    return value.map(item => {
      if (typeof item === 'object' && item !== null) {
        const inner = toStructuredText(item, indent + 1).trimStart();
        return `${pad}- ${inner}`;
      }
      const scalar = toStructuredText(item, 0).trimStart();
      return `${pad}- ${scalar}`;
    }).join('\n');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return `${pad}{}`;
    return entries.map(([k, v]) => {
      if (v === null || v === undefined) return `${pad}${k}: null`;
      if (typeof v === 'object') {
        const inner = toStructuredText(v, indent + 1);
        return `${pad}${k}:\n${inner}`;
      }
      const scalar = toStructuredText(v, 0).trimStart();
      return `${pad}${k}: ${scalar}`;
    }).join('\n');
  }
  return `${pad}${String(value)}`;
}

/**
 * Strip default/empty values so the preview shows only fields the user set.
 */
function stripDefaults(state: TaskFormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(state)) {
    if (val === '' || val === null || val === undefined) continue;
    if (Array.isArray(val) && val.length === 0) continue;
    if (val === false) continue;
    if (val === 0 && key !== 'priorityScore') continue;
    if (typeof val === 'object' && !Array.isArray(val)) {
      const entries = Object.entries(val as Record<string, unknown>);
      if (entries.every(([, v]) => !v || v === '' || v === false)) continue;
    }
    out[key] = val;
  }
  return out;
}

export const TaskFormToolbar: React.FC<TaskFormToolbarProps> = ({
  collectCurrentState,
  onClone,
  onSaveTemplate,
  onLoadTemplate,
  onLlmPrefill,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [format, setFormat] = useState<'json' | 'structured'>('json');

  const handleClone = () => {
    if (!onClone) return;
    const state = collectCurrentState();
    const cloned: TaskFormState = { ...state, name: `${state.name} (Clone)` };
    onClone(cloned);
  };

  const getPreviewContent = useCallback(() => {
    const state = collectCurrentState();
    const clean = stripDefaults(state);
    if (format === 'structured') return toStructuredText(clean);
    return JSON.stringify(clean, null, 2);
  }, [collectCurrentState, format]);

  const previewContent = previewOpen ? getPreviewContent() : '';

  return (
    <>
      <Group gap="xs">
        {onClone && (
          <Tooltip label="Clone this task" withArrow position="bottom">
            <ActionIcon variant="light" color="blue" size="lg" onClick={handleClone}>
              <IconCopy size={18} />
            </ActionIcon>
          </Tooltip>
        )}

        {onSaveTemplate && (
          <Tooltip label="Save as template" withArrow position="bottom">
            <ActionIcon variant="light" color="teal" size="lg" onClick={onSaveTemplate}>
              <IconDeviceFloppy size={18} />
            </ActionIcon>
          </Tooltip>
        )}

        {onLoadTemplate && (
          <Tooltip label="Load template" withArrow position="bottom">
            <ActionIcon variant="light" color="grape" size="lg" onClick={onLoadTemplate}>
              <IconFileImport size={18} />
            </ActionIcon>
          </Tooltip>
        )}

        {onLlmPrefill && (
          <Tooltip label="LLM auto-fill" withArrow position="bottom">
            <ActionIcon variant="light" color="violet" size="lg" onClick={onLlmPrefill}>
              <IconSparkles size={18} />
            </ActionIcon>
          </Tooltip>
        )}

        <Tooltip label="Preview template" withArrow position="bottom">
          <ActionIcon variant="light" color="gray" size="lg" onClick={() => setPreviewOpen(true)}>
            <IconBraces size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* JSON / Structured Preview Drawer */}
      <Drawer
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={
          <Group gap="sm">
            <Text fw={600}>Task Template Preview</Text>
            <SegmentedControl
              size="xs"
              value={format}
              onChange={(v) => setFormat(v as 'json' | 'structured')}
              data={[
                { label: 'JSON', value: 'json' },
                { label: 'Structured', value: 'structured' },
              ]}
            />
            <CopyButton value={previewContent}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy to clipboard'}>
                  <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} size="sm" onClick={copy}>
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        }
        position="right"
        size="lg"
        padding="md"
      >
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Non-default fields only. Empty/false values hidden.
          </Text>
          <ScrollArea h="calc(100vh - 120px)">
            <pre style={{ fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
              {previewContent}
            </pre>
          </ScrollArea>
        </Stack>
      </Drawer>
    </>
  );
};

export default TaskFormToolbar;
